import React, {useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import moment from "moment";
import "../../assets/css/result.css";
import "react-datepicker/dist/react-datepicker.css";
import {getLotteryResult} from "../../api/resultApi";
import ResultRenderer from "../../components/result/ResultRenderer";
import { getDateDef } from "../../utility/dateUtils";
import { LOTTERY_TABS } from "../../constant/lotteryTypes";

import { normalizeResultBrand, isValidResultBrand } from "../../components/result/resultAssets";

const getToday = () => getDateDef(new Date());

const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today;
};
const toDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
};

const DatePickerTrigger = React.forwardRef(({ value, onClick, className }, ref) => (
    <button
        ref={ref}
        type="button"
        className={className}
        onClick={onClick}
        aria-label={`Choose result date${value ? `, currently ${value}` : ""}`}
    >
        {value}
    </button>
));
DatePickerTrigger.displayName = "DatePickerTrigger";

const toFilterDate = (value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getPlatformKey = () => {
    if (typeof navigator === "undefined") return "other";

    const platformSource = [
        navigator.userAgentData?.platform,
        navigator.platform,
        navigator.userAgent,
    ]
        .filter(Boolean)
        .join(" ");

    if (/mac/i.test(platformSource)) return "mac";
    if (/win/i.test(platformSource)) return "windows";

    return "other";
};

export default function ResultPage({ hideBackButton: propHideBackButton, basePath: propBasePath }) {
    const {lotteryType, type, brand: routeBrand} = useParams();
    const brand = normalizeResultBrand(routeBrand);
    const hasValidBrand = isValidResultBrand(brand);
    const location = useLocation();
    const navigate = useNavigate();

    const currentLotteryType = lotteryType || type;
    const activeLotteryType = String(currentLotteryType || "").toLowerCase();

    const isAppPath = location.pathname.startsWith("/app/");
    const hideBackButton = propHideBackButton ?? isAppPath;
    const basePath = propBasePath || (isAppPath
        ? `/app${routeBrand ? `/${brand}` : ""}`
        : `/${brand}`);

    const searchParams = new URLSearchParams(location.search);
    const hasDrawAt = searchParams.has("drawAt");
    const hasDrawingId = searchParams.has("drawingId");
    const isSingleDraw = isAppPath && (hasDrawAt || hasDrawingId);
    const drawingId = searchParams.get("drawingId") || "";
    // Match the original API timestamp as an instant; format12h is for display only.
    const requestedDrawAt = searchParams.get("drawAt") || "";
    const parsedDrawAt = moment.parseZone(requestedDrawAt, moment.ISO_8601, true);
    const hasValidDrawAt = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(requestedDrawAt) && parsedDrawAt.isValid();
    const requestedDate = searchParams.get("filterByDate") || "";
    const hasValidDrawParams = !isSingleDraw || (
        (hasDrawAt
            ? !hasDrawingId && searchParams.getAll("drawAt").length === 1 && hasValidDrawAt
            : searchParams.getAll("drawingId").length === 1 && /^[1-9]\d*$/.test(drawingId)) &&
        searchParams.getAll("filterByDate").length === 1 &&
        moment(requestedDate, "YYYY-MM-DD", true).isValid()
    );

    const [filterDate, setFilterDate] = useState(getToday());
    const resultDate = isSingleDraw ? requestedDate : filterDate;
    const platformKey = useMemo(() => getPlatformKey(), []);

    const {
        data = [],
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ["lottery-result", currentLotteryType, resultDate],
        queryFn: () =>
            getLotteryResult({
                lotteryType: currentLotteryType,
                filterDate: resultDate,
            }),
        enabled: Boolean(hasValidBrand && currentLotteryType && resultDate && hasValidDrawParams),
        staleTime: 30000,
        refetchOnWindowFocus: false,
    });

    const sortedData = useMemo(() => {
        return [...data].sort(
            (a, b) => new Date(a.drawAt) - new Date(b.drawAt)
        );
    }, [data]);

    const visibleData = isSingleDraw
        ? (hasValidDrawParams ? sortedData.filter((item) => hasDrawAt
            ? moment.parseZone(item.drawAt, moment.ISO_8601, true).valueOf() === parsedDrawAt.valueOf()
            : String(item.drawingId) === drawingId).slice(0, 1) : [])
        : sortedData;

    if (!hasValidBrand) return <div className="result-page"><p role="alert">Invalid result brand.</p></div>;

    return (
        <div className="result-page">
            {!isSingleDraw && (
                <div className={`result-toolbar${hideBackButton ? " has-no-back" : ""}`}>
                    {!hideBackButton && (
                        <button
                            type="button"
                            className="result-toolbar-button result-toolbar-button-back"
                            onClick={() => navigate("/")}
                        >
                            Back
                        </button>
                )}

                <div className="result-toolbar-heading">
                    <div className="result-toolbar-tabs" role="tablist" aria-label="Lottery types">
                        {LOTTERY_TABS.map((item) => {
                            const isActive = activeLotteryType === item.value;

                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`result-toolbar-tab${isActive ? " is-active" : ""}`}
                                    onClick={() => navigate(`${basePath}/${item.value}`)}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="result-toolbar-controls">
                    <label className="result-toolbar-date-field">
                        <div className="result-toolbar-date-input-wrap">
                            <DatePicker
                                selected={toDateValue(filterDate)}
                                onChange={(value) => setFilterDate(toFilterDate(value))}
                                maxDate={getTodayDate()}
                                dateFormat="dd MMM yyyy"
                                className="result-toolbar-date-input"
                                customInput={<DatePickerTrigger />}
                                calendarClassName="result-toolbar-calendar"
                                popperClassName="result-toolbar-popper"
                                popperPlacement="bottom-end"
                                showPopperArrow={false}
                            />
                            <span className="result-toolbar-date-icon" aria-hidden="true">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7 2V5M17 2V5M3 9H21M5 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7C3 5.89543 3.89543 5 5 5Z"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </div>
                    </label>

                    <button
                        type="button"
                        className="result-toolbar-button result-toolbar-button-refresh"
                        onClick={refetch}
                        disabled={isFetching}
                    >
                        <span className="result-toolbar-refresh-icon" aria-hidden="true">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M20 12A8 8 0 1 1 17.66 6.34M20 4V10H14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className="result-toolbar-refresh-label">
                            {isFetching ? "Refreshing..." : "Refresh"}
                        </span>
                    </button>
                </div>
            </div>
            )}

            {!hasValidDrawParams && <p role="alert">Invalid draw link. Provide filterByDate in YYYY-MM-DD format and either drawAt as a full timestamp with timezone or a positive drawingId.</p>}
            {hasValidDrawParams && isLoading && <p>Loading...</p>}
            {hasValidDrawParams && isError && <p>{error?.message}</p>}

            {hasValidDrawParams && !isLoading && (!isSingleDraw || !isError) && visibleData.length === 0 && (
                <p>{isSingleDraw ? "No result for this draw" : "No result"}</p>
            )}

            <div className="result-list">
                {visibleData.map((item) => (
                    <ResultRenderer
                        key={item.drawingId}
                        brand={brand}
                        lotteryType={currentLotteryType}
                        item={item}
                        platformKey={platformKey}
                        sendImage={isSingleDraw && hasValidDrawParams && !isFetching && !isError && searchParams.get("sendImage") === "1"}
                    />
                ))}
            </div>
        </div>
    );
}
